import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { Amplify, Auth } from 'aws-amplify';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/browser';
import { httpClientIntegration } from '@sentry/integrations';
import { MsalProvider } from '@azure/msal-react';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import 'react-tooltip/dist/react-tooltip.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { logout, ThemeProvider } from '@qwealth/qcore';
import { QContext } from '@qwealth/qdata';

import { msalInstance } from 'services/microsoft/AuthService';
import store from './data/store';
import App from './App';

import './index.css';
import { errorHandler } from './services/axiosService';

const { REACT_APP_VERSION, REACT_APP_ENV } = process.env;

// eslint-disable-next-line no-console
console.log(`Current app version is: ${REACT_APP_VERSION}`);

QContext.setErrorHandler(errorHandler);

Sentry.init({
  dsn: 'https://c2cf0351b5c64af5a3afa0923db9e4c6@o1168654.ingest.sentry.io/6264115',
  integrations: [new BrowserTracing(), httpClientIntegration()],
  tracesSampleRate: 1.0,
  environment: REACT_APP_ENV,
  sendDefaultPii: true
});

const {
  REACT_APP_QWEALTH_AUTH_REGION,
  REACT_APP_QWEALTH_AUTH_USER_POOL_ID,
  REACT_APP_QWEALTH_AUTH_CLIENT_ID,
  REACT_APP_QWEALTH_S3_BUCKET,
  REACT_APP_QWEALTH_IDENTITY_POOL_ID,
} = process.env;

Amplify.configure({
  Auth: {
    identityPoolId: REACT_APP_QWEALTH_IDENTITY_POOL_ID,
    region: REACT_APP_QWEALTH_AUTH_REGION,
    userPoolId: REACT_APP_QWEALTH_AUTH_USER_POOL_ID,
    userPoolWebClientId: REACT_APP_QWEALTH_AUTH_CLIENT_ID,
  },
  Storage: {
    AWSS3: {
      bucket: REACT_APP_QWEALTH_S3_BUCKET,
      region: REACT_APP_QWEALTH_AUTH_REGION,
    },
  },
});

setTimeout(async () => {
  await logout();
  await Auth.signOut({ global: true });
  alert('Session expired. Please close ALL QWealth apps/sessions.');
}, 28800000);

setTimeout(() => {
  alert(
    'Your session is about to expire. Please save your work and close all QWealth apps/sessions.',
  );
}, 28740000);

const { REACT_APP_LD_CLIENT_ID } = process.env;

const AppComponent = REACT_APP_LD_CLIENT_ID
  ? withLDProvider({ clientSideID: REACT_APP_LD_CLIENT_ID })(App)
  : App;

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <ThemeProvider>
        <Router>
          <Provider store={store}>
            <AppComponent />
          </Provider>
        </Router>
      </ThemeProvider>
    </MsalProvider>
  </React.StrictMode>);
